import { t } from "i18next";

import { Clear } from "@mui/icons-material";
import { Box, Button, IconButton, SxProps, TextField } from "@mui/material";

import { distinctFillColor } from "@/src/theme/helpers";

import { dealerActiveAuctionSearchTermsAtom } from "@/src/state/dealer/dealerActiveAuctionSearchTermsAtom";
import { dealerEndedAuctionSearchTermsAtom } from "@/src/state/dealer/dealerEndedAuctionSearchTermsAtom";
import { dealerSearchBarContentAtom } from "@/src/state/dealer/dealerSearchBarContentAtom";

import { useAtom, useSetAtom } from "jotai";

interface SearchPanelProps {
  advancedSearchMode?: boolean;
  activeAuctions?: boolean;
  onSearch?: () => void;
  sx?: SxProps;
}

const SearchPanel = (props: SearchPanelProps) => {
  const setEndedSearchTerms = useSetAtom(dealerEndedAuctionSearchTermsAtom);
  const setActiveSearchTerms = useSetAtom(dealerActiveAuctionSearchTermsAtom);
  const setSearchTerms = props.activeAuctions
    ? setActiveSearchTerms
    : setEndedSearchTerms;

  const [searchBarContent, setSearchBarContent] = useAtom(
    dealerSearchBarContentAtom
  );

  const cleanedSearchBarContent = searchBarContent.trim();
  const isDisabled = cleanedSearchBarContent.length < 2;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: (muiTheme) => distinctFillColor(muiTheme),
          pt: 4,
          pb: 4,
          pl: [2, 3],
          pr: [2, 3],
          ...props.sx
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "50rem",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2
          }}
        >
          <TextField
            id="searchTermsInput"
            placeholder={`${t("components.searchPanel.inputPlaceholder")}`}
            value={searchBarContent}
            onChange={(e) => {
              const value = e.target.value;
              setSearchBarContent(value);
              if (value === "") {
                setSearchTerms("");
              }
            }}
            sx={(muiTheme) => ({
              width: "100%",
              backgroundColor: muiTheme.palette.background.paper
            })}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (!isDisabled) {
                  setSearchTerms(searchBarContent);
                  props.onSearch?.();
                }
              }
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <IconButton
                    id="searchPanelClearButton"
                    sx={{
                      visibility: searchBarContent !== "" ? "visible" : "hidden"
                    }}
                    onClick={() => {
                      setSearchBarContent("");
                      setSearchTerms("");
                    }}
                  >
                    <Clear />
                  </IconButton>
                )
              }
            }}
          />
          <Button
            id="searchPanelSearchButton"
            variant="contained"
            color="primary"
            onClick={() => {
              if (!isDisabled) {
                setSearchTerms(searchBarContent);
                props.onSearch?.();
              }
            }}
            disabled={isDisabled}
            sx={{ width: "8rem" }}
          >
            {t("components.searchPanel.searchButtonLabel")}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default SearchPanel;
